import { useContext, useState, useRef, useEffect } from "react"
import axios from "axios"
import { Button } from "primereact/button"
import { Toast } from "primereact/toast"
import FollowAlongSupportForm from "./FollowAlongSupportForm"
import {
  showError,
  Actions,
  renderTableDataCell,
  formatDate,
} from "../Utils/Utils"
import JobSearchPlan from "../Job/JobSearchPlanForm"
import { UserContext } from "../../../context/userContext"
import { employmentApiUrl } from "../../../config/microservicesUrls"
import styles from "../../../styles/Participant-Employment.module.css"
import { LoaderMedium } from "../../../components/Loaders"
import { Statuses, FollowAlongSupportTypes } from "../Types/Dropdowns"
import NoData from "../Utils/NoData"
import { ReactComponent as TaskCompleteIcon } from "../resources/task_complete.svg"
import FollowAlongSupportView from "./FollowAlongSupportView"
import ViewHeader from "../Utils/ViewHeader"

const api_url = employmentApiUrl()

export default function FollowAlongSupportIndex({
  inactive,
  viewMode,
  closeViewMode,
}) {
  const toast = useRef(null)
  const userCtx = useContext(UserContext)
  const [followAlongSupports, setFollowAlongSupports] = useState([])
  const [loading, setLoading] = useState(true)
  const [action, setAction] = useState(null)
  const [showFollowAlongSupportForm, setShowFollowAlongSupportForm] =
    useState(false)
  const [followAlongSupportRecord, setFollowAlongSupportRecord] = useState(null)
  const [showJobSearchPlanForm, setShowJobSearchPlanForm] = useState(false)
  const [hasJobSearchPlan, setHasJobSearchPlan] = useState(false)
  const [jobSearchPlanId, setJobSearchPlanId] = useState(false)

  const referralId = viewMode
    ? viewMode?.referral.id
    : userCtx.currentActiveReferral

  const getFollowAlongSupports = () => {
    setLoading(true)
    axios
      .get(
        `${api_url}/followAlongSupport/${referralId}/${userCtx.session.user.organization_id}/${userCtx.participant_id}`,
        {
          headers: {
            Authorization: `Bearer ${userCtx.tokens.getEmploymentToken()}`,
          },
        },
      )
      .then(async (response) => {
        if (response && response.data) {
          const followAlongSupports = response.data.followAlongSupports
          const jobSearchPlan = response.data.jobSearchPlan
          setFollowAlongSupports(followAlongSupports ? followAlongSupports : [])
          setHasJobSearchPlan(jobSearchPlan || followAlongSupports)
          const searchPlanId = followAlongSupports
            ? followAlongSupports[0].jobSearchPlanId
            : jobSearchPlan
              ? jobSearchPlan.id
              : null
          setJobSearchPlanId(searchPlanId)
        } else {
          setFollowAlongSupports([])
          setHasJobSearchPlan(false)
        }
        setLoading(false)
      })
      .catch(function (response) {
        const errors = response["response"]["data"]["errors"]
        setFollowAlongSupports([])
        setLoading(false)
        showError(toast, "Not able to get follow along supports", errors)
      })
  }

  useEffect(() => {
    getFollowAlongSupports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.participant_id])

  const renderSupportActions = (item) => {
    return (
      <div className={styles.table_actions}>
        <Button
          icon="pi pi-eye"
          className={styles.rounded_btn}
          onClick={() => {
            setAction(Actions.View)
            setFollowAlongSupportRecord(item)
            setShowFollowAlongSupportForm(true)
          }}
        />
        <Button
          className={styles.edit_btn}
          onClick={() => {
            setAction(Actions.Edit)
            setFollowAlongSupportRecord({ ...item })
            setShowFollowAlongSupportForm(true)
          }}
          disabled={inactive || viewMode?.view}
        />
      </div>
    )
  }

  const renderNoJobSearchPlan = () => {
    return (
      <div className={styles.no_record_div}>
        <NoData
          icon={
            <TaskCompleteIcon
              style={{ padding: "8px", width: "100%", height: "100%" }}
            />
          }
        />
        <div style={{ width: "600px" }}>
          <p style={{ fontWeight: "700" }}>
            A job search plan must be completed before adding jobs
          </p>
        </div>
        <Button
          className={styles.button}
          label="Create job search plan"
          onClick={() => setShowJobSearchPlanForm(true)}
          disabled={inactive || viewMode?.view}
        />
      </div>
    )
  }

  const renderIndex = () => {
    const desc1 =
      "One of the key principles of supported employment is the provision of supports " +
      "to help people be successful with their jobs and advance their careers after securing employment. " +
      "Follow along supports can be provided by a wide range of people including employment specialists, " +
      "peers, case managers, psychiatrists, family members and other supportive people or organization."
    const desc2 =
      "Follow along supports can also take many forms such as an in-person meeting, a phone " +
      "call, or a visit to the person's workplace with permission. This form should be used to to consider " +
      "potential options in planning and providing follow along supports. In each area, add as many forms of " +
      "follow along supports that may be provided or needed and who may also provide those supports."

    return (
      <>
        {loading && (
          <div style={{ width: "100%" }}>
            <LoaderMedium />
          </div>
        )}
        {!loading && (
          <>
            {!hasJobSearchPlan && renderNoJobSearchPlan()}
            {hasJobSearchPlan && (
              <div className={styles.rounded_wrapper}>
                <ViewHeader
                  closeViewMode={closeViewMode}
                  title={"Follow along supports"}
                  viewMode={viewMode}
                />
                <p>{desc1}</p>
                <p>{desc2}</p>
                <div className={styles.followContainerAddBtn}>
                  <Button
                    label="Add a follow along support"
                    className={styles.addFollowBtn}
                    onClick={() => {
                      setAction(Actions.Add)
                      setFollowAlongSupportRecord(null)
                      setShowFollowAlongSupportForm(true)
                    }}
                    disabled={inactive || viewMode?.view}
                  />
                </div>
                <table id="followAlongSupports" className={styles.table}>
                  <thead>
                    <tr>
                      <th key="date_created_th" style={{ minWidth: "12%" }}>
                        Date created
                      </th>
                      <th key="last_update_th" style={{ minWidth: "12%" }}>
                        Last update
                      </th>
                      <th
                        key="providers_support_th"
                        style={{ minWidth: "26%" }}
                      >
                        Provider(s) of support
                      </th>
                      <th
                        key="type_of_follow_along_support_th"
                        style={{ minWidth: "26%" }}
                      >
                        Type of follow-along support
                      </th>
                      <th key="status_th" style={{ minWidth: "10%" }}>
                        Status
                      </th>
                      <th key="actions_th" />
                    </tr>
                  </thead>
                  <tbody>
                    {followAlongSupports.length > 0 ? (
                      followAlongSupports?.map((item, index) => {
                        return (
                          <tr key={index}>
                            {renderTableDataCell({
                              label: "createdAt",
                              value: item.createdAt,
                              type: "date",
                            })}
                            {renderTableDataCell({
                              label: "updatedAt",
                              value: item.updatedAt,
                              type: "date",
                            })}
                            {renderTableDataCell({
                              label: "providerOfSupport",
                              value: item.providerOfSupport,
                              type: "string",
                            })}
                            {renderTableDataCell({
                              label: "supportType",
                              value: item.supportType
                                ? FollowAlongSupportTypes[item.supportType]
                                : "None",
                              type: "string",
                            })}
                            <td key="status">
                              {item.status !== null && (
                                <span
                                  className={`${styles.status_chip} ${
                                    item.status === 0
                                      ? styles.status_active
                                      : styles.status_inactive
                                  }`}
                                >
                                  {Statuses[item.status]}
                                </span>
                              )}
                            </td>
                            <td>{renderSupportActions(item)}</td>
                          </tr>
                        )
                      })
                    ) : (
                      <td colSpan="6" style={{ paddingTop: "10px" }}>
                        {" "}
                        No supports have been added
                      </td>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </>
    )
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      {showFollowAlongSupportForm && viewMode?.view && (
        <FollowAlongSupportView
          followAlongSupport={followAlongSupportRecord}
          loading={loading}
          closeForm={() => setShowFollowAlongSupportForm(false)}
          viewMode={viewMode}
        />
      )}
      {showFollowAlongSupportForm && !viewMode?.view && (
        <FollowAlongSupportForm
          closeForm={() => setShowFollowAlongSupportForm(false)}
          action={action}
          followAlongSupportRecord={followAlongSupportRecord}
          jobSearchPlanId={jobSearchPlanId}
          updateData={(data) => {
            if (action === Actions.Add) {
              setFollowAlongSupports([...followAlongSupports, data])
            } else {
              const updatedRecords = followAlongSupports?.map((r) => {
                if (r.id === data.id) {
                  return data
                }
                return r
              })
              setFollowAlongSupports(updatedRecords)
            }
          }}
        />
      )}
      {!showFollowAlongSupportForm && !showJobSearchPlanForm && renderIndex()}
      {showJobSearchPlanForm && (
        <JobSearchPlan
          closeForm={() => setShowJobSearchPlanForm(false)}
          action={Actions.Add}
          setSearchGoalDefaultTargetDate={(date) => {}}
          minTargetDate={formatDate(new Date())}
          updateData={(data) => {
            setHasJobSearchPlan(true)
          }}
        />
      )}
    </>
  )
}
